import { useEffect, useState } from 'react';
import { FormSection } from '@frontend/components/elements/forms';
import type { IEpisodesAndJournals } from '../../../../common/validation/episodesAndJournal';
import type { ValidationErrors } from '../../../utils/validation';
import type { IAllergy } from '../../interfaces/allergy';
import type { LabResult } from '../../interfaces/labresult';
import { ProcessEntity } from '../../../../common/enums/processEntity';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { AllergiesAddEdit } from '../customControls/utils/v1/allergies/AllergiesAddEdit';
import { EpisodeJournalsAddEdit } from '../customControls/utils/v1/episodeJournals/EpisodeJournalsAddEdit';
import { LabResultsAddEdit } from '../customControls/utils/v1/labResults/LabResultsAddEdit';

interface IProfessionalSummaryDataProps {
    entity: ProcessEntity.request | ProcessEntity.procedure;
    entityId?: string;
    errors?: ValidationErrors;
    handleChangeAllergies: (allergies: IAllergy[]) => Promise<void>;
    handleChangeCustomControl?: <T = any>(values: Partial<T>) => Promise<void>;
    handleChangeEpisodes: (episodesAndJournals: IEpisodesAndJournals) => Promise<void>;
    handleChangeLabResults: (labResults: LabResult[]) => Promise<void>;
    requestId?: string;
    validateEpisodesAndJournals: () => Promise<void>;
    validateOneCustomControl?: (name: string, index?: number) => Promise<void>;
}

export function ProfessionalSummaryData(props: IProfessionalSummaryDataProps) {
    const { patient } = useEpisodeContext();
    const [isHisDataAvailable, setIsHisDataAvailable] = useState<boolean>();

    useEffect(() => {
        loadIsHisDataAvailable();
    }, []);

    if (!isHisDataAvailable) {
        return null;
    }

    return (
        <FormSection title={translateKey('professionalSummary.professionalSummary')}>
            <AllergiesAddEdit
                {...props}
                onChange={async (allergies) => await props.handleChangeAllergies(allergies)}
                patient={patient}
                requestId={props.entity === ProcessEntity.request ? props.entityId : props.requestId}
            />

            <LabResultsAddEdit
                {...props}
                onChange={async (labResults) => await props.handleChangeLabResults(labResults)}
                patient={patient}
                requestId={props.entity === ProcessEntity.request ? props.entityId : props.requestId}
            />

            <EpisodeJournalsAddEdit
                {...props}
                onChange={async (episodesAndJournal) => await props.handleChangeEpisodes(episodesAndJournal)}
                patient={patient}
                requestId={props.entity === ProcessEntity.request ? props.entityId : props.requestId}
                variant="flushed"
            />
        </FormSection>
    );

    async function loadIsHisDataAvailable() {
        let url = `/api/patients/${patient.id}/hisDataAvailability`;
        if (props.requestId) {
            url += `?requestId=${props.requestId}`;
        }
        const result = await fetchJson(url, 'GET');
        setIsHisDataAvailable(result.json.result);
    }
}
